import { Configuration, PublicClientApplication } from "@azure/msal-browser";

import ICustomError from "../../components/types/ICustomError";
import Services from "../services";
import { assertNonNullish } from "@tedivo/tedivo-pure-helpers";
import { getTranslation } from "../i18/i18tn";
import { waitForCondition } from "@tedivo/tedivo-dom-helpers";

class EntraIdFederationProvider {
  public publicClientApp: PublicClientApplication | undefined = undefined;

  constructor() {
    const appId = process.env.NX_PUBLIC_ENTRAID_APPID;

    if (!appId) {
      console.error("EntraIdFederationProvider will not be initialized");
      return;
    }

    const config: Configuration = {
      auth: {
        clientId: appId,
        redirectUri: `${window.location.origin}/oauth-response`,
        postLogoutRedirectUri: window.location.origin,
      },
    };

    // create PublicClientApplication instance
    this.publicClientApp = new PublicClientApplication(config);
    this.publicClientApp.initialize().then(() => {
      this.publicClientApp?.clearCache().catch(console.error);
    });
  }

  // public loginWithRedirect = async () => {
  //   assertNonNullish(
  //     this.publicClientApp,
  //     getTranslation("errors:ENTRAID.publicClientApplicationNotInitialized"),
  //   );

  //   const handleResponse = (response: AuthenticationResult | null) => {
  //     console.log("handleResponse:", response);

  //     if (response !== null) {
  //       this.accountId = response.account.homeAccountId;
  //       // Display signed-in user content, call API, etc.
  //     } else {
  //       assertNonNullish(
  //         this.publicClientApp,
  //         getTranslation(
  //           "errors:ENTRAID.publicClientApplicationNotInitialized",
  //         ),
  //       );

  //       // In case multiple accounts exist, you can select
  //       const currentAccounts = this.publicClientApp.getAllAccounts();

  //       if (currentAccounts.length === 0) {
  //         const loginRequest: RedirectRequest = {
  //           scopes: ["openid"],
  //         };

  //         console.log("loginRequest:", JSON.stringify(loginRequest, null, 2));

  //         // no accounts signed-in, attempt to sign a user in
  //         this.publicClientApp.loginRedirect(loginRequest);
  //       } else if (currentAccounts.length > 1) {
  //         // Add choose account code here
  //       } else if (currentAccounts.length === 1) {
  //         this.accountId = currentAccounts[0].homeAccountId;
  //       }
  //     }
  //   };

  //   this.publicClientApp.handleRedirectPromise().then(handleResponse);

  // };

  public loginWithPopup = async (
    email: string | undefined,
  ): Promise<
    | {
        email: string;
        customAuthCode: string;
      }
    | undefined
  > => {
    assertNonNullish(
      this.publicClientApp,
      getTranslation("errors:ENTRAID.publicClientApplicationNotInitialized"),
    );

    if (!email) {
      throw new Error("Email is required");
    }

    try {
      await waitForCondition(() => this.isInitialized, 1000);

      // Request scopes
      const loginRequest = {
        scopes: [process.env.NX_PUBLIC_ENTRAID_SCOPE || ""],
        select_account: true,
        loginHint: email,
      };

      // Msal loginPopup
      const loginResponse = await this.publicClientApp.loginPopup(loginRequest);

      // Check if login was successful
      if (!loginResponse || !loginResponse.account) {
        throw new Error("Login using Entra ID was not successful");
      }

      // Check if email is valid
      if (
        email.trim().toLowerCase() !==
        loginResponse.account?.username?.trim().toLowerCase()
      ) {
        throw new Error("Invalid email");
      }

      // Check if audience is valid
      // OIDC allows aud to be a string or string[]; MSAL may return either.
      // A strict !== against a string always fails for an array, rejecting valid tokens.
      const aud = loginResponse.account?.idTokenClaims?.aud;
      const clientId = this.publicClientApp?.getConfiguration().auth.clientId;
      const audValid = Array.isArray(aud) ? aud.includes(clientId) : aud === clientId;
      if (!aud || !audValid) {
        throw new Error("Invalid audience");
      }

      // Check if token is expired
      const secondsToExpire =
        (loginResponse.account?.idTokenClaims?.exp || 0) - Date.now() / 1000;

      if (secondsToExpire < 0) {
        throw new Error("Token expired");
      }

      // Check if there is an access token
      if (!loginResponse.accessToken) {
        throw new Error("Access token is required");
      }

      // Pass token to API to verify it
      const res = await Services.accounts.passEntraIdAccessToken(
        loginResponse.accessToken,
        email,
      );

      return res.data;
    } catch (error) {
      let errorMessage =
        typeof error === "string" ? error : (error as Error).message;

      const stripPrefix = (s: string) => s.replace(/^\s*[A-Za-z0-9_]+:\s*/, "");
      errorMessage = stripPrefix(errorMessage);

      document.body.dispatchEvent(
        new CustomEvent<ICustomError>("customError", {
          detail: {
            message: getTranslation("auth:logIn"),
            translationKey: errorMessage || "errors:errorHasOcurred",
            errorCode: "0",
          },
        }),
      );
    }
  };

  public logout = () => {
    assertNonNullish(
      this.publicClientApp,
      getTranslation("errors:ENTRAID.publicClientApplicationNotInitialized"),
    );
    this.publicClientApp.logoutRedirect();
  };

  // public getAccessTokenFromCode = async (code: string) => {
  //   assertNonNullish(
  //     this.publicClientApp,
  //     getTranslation("errors:ENTRAID.publicClientApplicationNotInitialized"),
  //   );

  //   const config = this.publicClientApp.getConfiguration();
  //   const appId = config.auth.clientId;
  //   if (!appId) {
  //     throw new Error("App ID is required");
  //   }

  //   const requestParamsStr = sessionStorage.getItem(
  //     `msal.${appId}.request.params`,
  //   );
  //   const requestCodeVerifier = sessionStorage.getItem(
  //     `msal.${appId}.code.verifier`,
  //   );

  //   assertNonNullish(requestParamsStr, "Request params are required");
  //   assertNonNullish(requestCodeVerifier, "Request code verifier is required");

  //   console.log(window.atob(requestParamsStr));

  //   const requestParams = JSON.parse(window.atob(requestParamsStr)) as {
  //     nonce: string;
  //     codeChallenge: string;
  //     state: string;
  //     correlationId: string;
  //   };

  //   // const base64url = (ab: ArrayBuffer) =>
  //   //   btoa(String.fromCharCode(...new Uint8Array(ab)))
  //   //     .replace(/\+/g, "-")
  //   //     .replace(/\//g, "_")
  //   //     .replace(/=+$/, "");

  //   // const d = await crypto.subtle.digest(
  //   //   "SHA-256",
  //   //   new TextEncoder().encode(requestCodeVerifier),
  //   // );

  //   const res = await Services.accounts.passEntraIdIdToken({
  //     code,
  //     nonce: requestParams.nonce,
  //     codeChallenge: requestParams.codeChallenge,
  //     codeVerifier: requestCodeVerifier,
  //     correlationId: requestParams.correlationId,
  //   });

  //   console.log("res:", JSON.stringify(res, null, 2));
  //   return res.data;
  // };

  get isInitialized() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (this.publicClientApp as any).controller.initialized ?? false;
  }
}

const entraIdFederationProvider = new EntraIdFederationProvider();
export { entraIdFederationProvider };
